<script setup lang="ts">
import EstateThumbSkeleton from '../thumb/EstateThumbSkeleton.vue'
import EstateThumb from '~/components/estate/thumb/EstateThumb.vue'
import ItemsSlider from '~/components/global/ItemsSlider.vue'
import { useEstateClosestData } from '~/composables/api/useEstateClosestData'
import type { EstateDetailInterface } from '~/types/api/EstateDetail'
import type { EstateClosestResponse } from '~/types/api/EstateClosest'

const props = defineProps<{
  estateData: EstateDetailInterface
}>()

const estateResponse = useAsyncData<EstateClosestResponse>(
  async () => {
    return await useEstateClosestData({
      estateData: props.estateData,
    })
  },
  {
    server: false,
  }
)
</script>

<template>
  <div class="c-estate-detail-similar">
    <h2 class="c-estate-detail-similar__title h2">
      {{
        $t(`pages.estateDetail.similar.title.${estateData.type}`, {
          _0: estateData.name,
        })
      }}
    </h2>

    <ItemsSlider
      v-if="estateResponse.pending.value"
      class="c-estate-detail-similar__items-skeleton"
      :slider-id="`estate-closest-skeleton-${estateData.id}-${estateData.type}`"
    >
      <SwiperSlide v-for="i in 3" :key="`skeleton-${i}`">
        <EstateThumbSkeleton :estate-type="estateData.type" />
      </SwiperSlide>
    </ItemsSlider>

    <ItemsSlider
      v-else-if="estateResponse.data.value?.data?.length"
      class="c-estate-detail-similar__items"
      :slider-id="`estate-closest-${estateData.id}-${estateData.type}`"
    >
      <SwiperSlide
        v-for="item in estateResponse.data.value?.data"
        :key="item.id"
      >
        <EstateThumb :estate-data="item" thumb-type="slider" />
      </SwiperSlide>
    </ItemsSlider>
  </div>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-estate-detail-similar {
  padding-top: 70px;

  @include breakpoint-down('sm') {
    padding-top: 48px;
  }

  &__title {
    margin-bottom: 42px;
    text-align: center;

    @include breakpoint-down('sm') {
      font-size: 32px;
      margin-bottom: 24px;
    }
  }

  &__items {
    @include breakpoint-down('md') {
      :deep(.c-estate-thumb) .c-estate-thumb-parameters {
        --estate-thumb-parameters--image-display: none;
        --estate-thumb-parameters--label--display: block;
      }
    }
  }

  &__items-skeleton {
    width: 100%;
  }
}
</style>
